import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../../api/api";
import Swal from "sweetalert2";

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  ArrowBack,
  CheckCircle,
  CloudUpload,
  Description,
  FilePresent,
  InfoOutlined,
  LibraryBooks,
  PictureAsPdf,
  Publish,
  Refresh,
  School,
  Subject as SubjectIcon,
  UploadFile,
  Visibility,
} from "@mui/icons-material";


const MAX_FILE_SIZE = 100 * 1024 * 1024;

const ALLOWED_EXTENSIONS = [
  "pdf",
  "doc",
  "docx",
  "xls",
  "xlsx",
  "ppt",
  "pptx",
];

const DOCUMENT_TYPES = [
  {
    value: "curriculum",
    label: "National Curriculum",
  },
  {
    value: "syllabus",
    label: "Syllabus",
  },
  {
    value: "teacher_guide",
    label: "Teacher Guide",
  },
  {
    value: "framework",
    label: "Curriculum Framework",
  },
  {
    value: "assessment",
    label: "Assessment Guide",
  },
  {
    value: "other",
    label: "Other",
  },
];


const getErrorMessage = (error) => {
  const data = error?.response?.data;

  if (!data) {
    return (
      error?.message ||
      "An unexpected error occurred. Please try again."
    );
  }

  if (typeof data === "string") {
    return data;
  }

  if (data.detail) {
    return data.detail;
  }

  if (typeof data === "object") {
    return Object.entries(data)
      .map(([key, value]) => {
        if (Array.isArray(value)) {
          return `${key}: ${value.join(", ")}`;
        }

        if (typeof value === "object") {
          return `${key}: ${JSON.stringify(value)}`;
        }

        return `${key}: ${value}`;
      })
      .join("\n");
  }

  return "Unable to complete the request.";
};


const normalizeItems = (response) => {
  const data = response?.data;

  if (Array.isArray(data)) {
    return data;
  }

  if (Array.isArray(data?.results)) {
    return data.results;
  }

  return [];
};


const CurriculumPost = ({
  onBack,
  onSuccess,
  editDocument = null,
}) => {
  const fileInputRef = useRef(null);

  const [academicYears, setAcademicYears] = useState([]);
  const [gradeClasses, setGradeClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);

  const [loadingAcademicYears, setLoadingAcademicYears] = useState(false);
  const [loadingGrades, setLoadingGrades] = useState(false);
  const [loadingSubjects, setLoadingSubjects] = useState(false);

  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");

  const [formErrors, setFormErrors] = useState({});

  const [form, setForm] = useState({
    title: "",
    document_code: "",
    document_type: "curriculum",
    description: "",
    academic_year: null,
    grade_class: null,
    subject: null,
    version: "1.0",
    revision_date: "",
    is_active: true,
    is_published: false,
  });


  const isEditing = Boolean(editDocument?.id);


  useEffect(() => {
    loadAcademicYears();
    loadGradeClasses();
    loadSubjects();
  }, []);


  useEffect(() => {
    if (!editDocument) {
      return;
    }

    setForm({
      title: editDocument.title || "",
      document_code: editDocument.document_code || "",
      document_type:
        editDocument.document_type || "curriculum",
      description: editDocument.description || "",
      academic_year:
        editDocument.academic_year_object ||
        editDocument.academic_year_data ||
        academicYears.find(
          (item) => item.id === editDocument.academic_year
        ) ||
        null,
      grade_class:
        editDocument.grade_class_object ||
        editDocument.grade_class_data ||
        gradeClasses.find(
          (item) => item.id === editDocument.grade_class
        ) ||
        null,
      subject:
        editDocument.subject_object ||
        editDocument.subject_data ||
        subjects.find(
          (item) => item.id === editDocument.subject
        ) ||
        null,
      version: editDocument.version || "1.0",
      revision_date: editDocument.revision_date || "",
      is_active:
        typeof editDocument.is_active === "boolean"
          ? editDocument.is_active
          : true,
      is_published:
        typeof editDocument.is_published === "boolean"
          ? editDocument.is_published
          : false,
    });
  }, [
    editDocument,
    academicYears,
    gradeClasses,
    subjects,
  ]);


  const loadAcademicYears = async () => {
    setLoadingAcademicYears(true);

    try {
      const response = await api.get("/academic-years/");
      setAcademicYears(normalizeItems(response));
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Academic Years",
        text: getErrorMessage(error),
      });
    } finally {
      setLoadingAcademicYears(false);
    }
  };


  const loadGradeClasses = async () => {
    setLoadingGrades(true);

    try {
      const response = await api.get("/api/users/grade-classes/");
      setGradeClasses(normalizeItems(response));
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Grade Classes",
        text: getErrorMessage(error),
      });
    } finally {
      setLoadingGrades(false);
    }
  };


  const loadSubjects = async () => {
    setLoadingSubjects(true);

    try {
      const response = await api.get("/api/users/subjects/");
      setSubjects(normalizeItems(response));
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Subjects",
        text: getErrorMessage(error),
      });
    } finally {
      setLoadingSubjects(false);
    }
  };


  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((previous) => ({
      ...previous,
      [name]: value,
    }));

    setFormErrors((previous) => ({
      ...previous,
      [name]: "",
    }));
  };


  const handleFileChange = (event) => {
    const selectedFile = event.target.files?.[0];

    setFileError("");
    setFile(null);

    if (!selectedFile) {
      return;
    }

    if (selectedFile.size > MAX_FILE_SIZE) {
      setFileError(
        "The selected file exceeds the maximum allowed size of 100 MB."
      );

      event.target.value = "";
      return;
    }

    const extension = selectedFile.name
      .split(".")
      .pop()
      ?.toLowerCase();

    if (!extension || !ALLOWED_EXTENSIONS.includes(extension)) {
      setFileError(
        "Unsupported file type. Allowed formats are PDF, Word, Excel and PowerPoint."
      );

      event.target.value = "";
      return;
    }

    setFile(selectedFile);
  };


  const validate = () => {
    const errors = {};

    if (!form.title.trim()) {
      errors.title = "Document title is required.";
    }

    if (!form.academic_year) {
      errors.academic_year = "Academic year is required.";
    }

    if (!form.grade_class) {
      errors.grade_class = "Grade/class is required.";
    }

    if (!form.version.trim()) {
      errors.version = "Version is required.";
    }

    if (!isEditing && !file) {
      errors.file = "Please select the curriculum document.";
    }

    if (file && file.size > MAX_FILE_SIZE) {
      errors.file =
        "The selected file exceeds the maximum allowed size of 100 MB.";
    }

    if (form.is_published && !file && !editDocument?.file) {
      errors.file =
        "A document file is required before publishing.";
    }

    setFormErrors(errors);

    return Object.keys(errors).length === 0;
  };


  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validate()) {
      Swal.fire({
        icon: "warning",
        title: "Incomplete Information",
        text: "Please review the highlighted fields.",
      });

      return;
    }

    setSaving(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();

      formData.append(
        "title",
        form.title.trim()
      );

      formData.append(
        "document_code",
        form.document_code.trim()
      );

      formData.append(
        "document_type",
        form.document_type
      );

      formData.append(
        "description",
        form.description.trim()
      );

      formData.append(
        "academic_year",
        form.academic_year.id
      );

      formData.append(
        "grade_class",
        form.grade_class.id
      );

      if (form.subject?.id) {
        formData.append(
          "subject",
          form.subject.id
        );
      } else {
        formData.append("subject", "");
      }

      formData.append(
        "version",
        form.version.trim()
      );

      if (form.revision_date) {
        formData.append(
          "revision_date",
          form.revision_date
        );
      }

      formData.append(
        "is_active",
        String(form.is_active)
      );

      formData.append(
        "is_published",
        String(form.is_published)
      );

      if (file) {
        formData.append(
          "file",
          file
        );
      }

      const config = {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
          if (!progressEvent.total) {
            return;
          }

          const progress = Math.round(
            (progressEvent.loaded * 100) /
              progressEvent.total
          );

          setUploadProgress(progress);
        },
      };

      let response;

      if (isEditing) {
        response = await api.patch(
          `/national-curriculum/${editDocument.id}/`,
          formData,
          config
        );
      } else {
        response = await api.post(
          "/national-curriculum/",
          formData,
          config
        );
      }

      Swal.fire({
        icon: "success",
        title: isEditing
          ? "Curriculum Updated"
          : "Curriculum Uploaded",
        text: isEditing
          ? "The curriculum document has been updated successfully."
          : "The curriculum document has been uploaded successfully.",
        confirmButtonColor: "#062A4B",
      });

      if (onSuccess) {
        onSuccess(response.data);
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Unable to Save Curriculum",
        text: getErrorMessage(error),
        confirmButtonColor: "#062A4B",
      });
    } finally {
      setSaving(false);
      setUploadProgress(0);
    }
  };


  const formatBytes = (bytes) => {
    if (!bytes) {
      return "0 Bytes";
    }

    const sizes = [
      "Bytes",
      "KB",
      "MB",
      "GB",
    ];

    const index = Math.floor(
      Math.log(bytes) / Math.log(1024)
    );

    return `${parseFloat(
      (bytes / Math.pow(1024, index)).toFixed(2)
    )} ${sizes[index]}`;
  };


  const currentFileName = useMemo(() => {
    if (file) {
      return file.name;
    }

    if (editDocument?.filename) {
      return editDocument.filename;
    }

    if (editDocument?.file) {
      return editDocument.file.split("/").pop();
    }

    return "";
  }, [file, editDocument]);


  return (
    <Box
      sx={{
        "--cayep-nav-navy": "#062A4B",
        "--cayep-nav-blue": "#063A6B",
        "--cayep-nav-black": "#0A0F12",
        "--cayep-nav-gold": "#F6C944",
        "--cayep-nav-yellow": "#EFA81C",
        "--cayep-nav-orange": "#C0750D",
        "--cayep-nav-green": "#4FAE22",
        "--cayep-nav-olive": "#5F6622",
        "--cayep-nav-sky": "#0F6DAB",
        "--cayep-nav-white": "#F0EDEA",
        "--cayep-nav-gray": "#C4C7C7",
        "--nav-white": "#ffffff",
        "--nav-text": "#142536",
        "--nav-muted": "#6b7280",
        "--nav-border": "#e7ebee",
        "--nav-soft": "#f7f9fa",
        "--nav-shadow":
          "0 12px 35px rgba(6, 42, 75, 0.10)",
        "--nav-transition": "220ms ease",

        minHeight: "100vh",
        background:
          "linear-gradient(135deg, #f7f9fa 0%, #eef3f5 100%)",
        p: {
          xs: 1.5,
          md: 3,
        },
      }}
    >
      <Box
        sx={{
          maxWidth: 1450,
          mx: "auto",
        }}
      >
        <Stack
          direction={{
            xs: "column",
            sm: "row",
          }}
          justifyContent="space-between"
          alignItems={{
            xs: "stretch",
            sm: "center",
          }}
          spacing={2}
          mb={3}
        >
          <Box>
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
            >
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background:
                    "linear-gradient(135deg, #062A4B, #0F6DAB)",
                  color: "#fff",
                  boxShadow:
                    "0 10px 24px rgba(6,42,75,.22)",
                }}
              >
                <LibraryBooks />
              </Box>

              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    color: "#062A4B",
                    letterSpacing: "-0.5px",
                  }}
                >
                  {isEditing
                    ? "Edit Curriculum"
                    : "Post Curriculum"}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: "#6b7280",
                    mt: 0.4,
                  }}
                >
                  Upload and manage official curriculum
                  resources for the school library.
                </Typography>
              </Box>
            </Stack>
          </Box>

          {onBack && (
            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
              onClick={onBack}
              sx={{
                borderColor: "#062A4B",
                color: "#062A4B",
                fontWeight: 700,
                borderRadius: 2,
                "&:hover": {
                  borderColor: "#0F6DAB",
                  background: "rgba(15,109,171,.06)",
                },
              }}
            >
              Back
            </Button>
          )}
        </Stack>


        <Grid
          container
          spacing={3}
        >
          <Grid
            item
            xs={12}
            lg={8}
          >
            <Card
              sx={{
                borderRadius: 3,
                border:
                  "1px solid rgba(6,42,75,.08)",
                boxShadow:
                  "0 12px 35px rgba(6,42,75,.10)",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  px: 3,
                  py: 2,
                  background:
                    "linear-gradient(135deg,#062A4B,#063A6B)",
                  color: "#fff",
                }}
              >
                <Stack
                  direction="row"
                  spacing={1.2}
                  alignItems="center"
                >
                  <Description />
                  <Box>
                    <Typography
                      fontWeight={800}
                    >
                      Curriculum Information
                    </Typography>

                    <Typography
                      variant="caption"
                      sx={{
                        opacity: 0.82,
                      }}
                    >
                      Provide the official curriculum
                      information below.
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Box
                  component="form"
                  onSubmit={handleSubmit}
                >
                  <Grid
                    container
                    spacing={2.2}
                  >
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Curriculum Title"
                        name="title"
                        value={form.title}
                        onChange={handleChange}
                        error={Boolean(formErrors.title)}
                        helperText={
                          formErrors.title ||
                          "Official title of the curriculum document."
                        }
                      />
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      md={6}
                    >
                      <TextField
                        fullWidth
                        label="Document Code"
                        name="document_code"
                        value={form.document_code}
                        onChange={handleChange}
                        helperText="Official reference or curriculum code."
                      />
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      md={6}
                    >
                      <FormControl fullWidth>
                        <TextField
                          select
                          label="Document Type"
                          name="document_type"
                          value={form.document_type}
                          onChange={handleChange}
                        >
                          {DOCUMENT_TYPES.map(
                            (type) => (
                              <MenuItem
                                key={type.value}
                                value={type.value}
                              >
                                {type.label}
                              </MenuItem>
                            )
                          )}
                        </TextField>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={4}
                        label="Description"
                        name="description"
                        value={form.description}
                        onChange={handleChange}
                        helperText="Brief description of the curriculum resource."
                      />
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      md={4}
                    >
                      <Autocomplete
                        options={academicYears}
                        value={form.academic_year}
                        loading={loadingAcademicYears}
                        onChange={(_, value) => {
                          setForm((previous) => ({
                            ...previous,
                            academic_year: value,
                          }));

                          setFormErrors(
                            (previous) => ({
                              ...previous,
                              academic_year: "",
                            })
                          );
                        }}
                        getOptionLabel={(option) =>
                          option?.name ||
                          option?.year ||
                          option?.academic_year ||
                          ""
                        }
                        isOptionEqualToValue={(
                          option,
                          value
                        ) =>
                          Number(option.id) ===
                          Number(value.id)
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Academic Year"
                            error={Boolean(
                              formErrors.academic_year
                            )}
                            helperText={
                              formErrors.academic_year
                            }
                          />
                        )}
                      />
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      md={4}
                    >
                      <Autocomplete
                        options={gradeClasses}
                        value={form.grade_class}
                        loading={loadingGrades}
                        onChange={(_, value) => {
                          setForm((previous) => ({
                            ...previous,
                            grade_class: value,
                          }));

                          setFormErrors(
                            (previous) => ({
                              ...previous,
                              grade_class: "",
                            })
                          );
                        }}
                        getOptionLabel={(option) =>
                          option?.name ||
                          option?.title ||
                          ""
                        }
                        isOptionEqualToValue={(
                          option,
                          value
                        ) =>
                          Number(option.id) ===
                          Number(value.id)
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Grade / Class"
                            error={Boolean(
                              formErrors.grade_class
                            )}
                            helperText={
                              formErrors.grade_class
                            }
                          />
                        )}
                      />
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      md={4}
                    >
                      <Autocomplete
                        options={subjects}
                        value={form.subject}
                        loading={loadingSubjects}
                        onChange={(_, value) => {
                          setForm((previous) => ({
                            ...previous,
                            subject: value,
                          }));
                        }}
                        getOptionLabel={(option) =>
                          option?.name ||
                          option?.title ||
                          ""
                        }
                        isOptionEqualToValue={(
                          option,
                          value
                        ) =>
                          Number(option.id) ===
                          Number(value.id)
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Subject"
                            helperText="Optional. Leave empty for a general grade curriculum."
                          />
                        )}
                      />
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      md={6}
                    >
                      <TextField
                        fullWidth
                        label="Version"
                        name="version"
                        value={form.version}
                        onChange={handleChange}
                        error={Boolean(formErrors.version)}
                        helperText={
                          formErrors.version ||
                          "Example: 1.0, 2.0, Revised 2026."
                        }
                      />
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      md={6}
                    >
                      <TextField
                        fullWidth
                        type="date"
                        label="Revision Date"
                        name="revision_date"
                        value={form.revision_date}
                        onChange={handleChange}
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                    </Grid>

                    <Grid item xs={12}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          borderRadius: 2.5,
                          borderColor:
                            "#dfe5e8",
                          background:
                            "#fbfcfc",
                        }}
                      >
                        <Stack
                          direction={{
                            xs: "column",
                            sm: "row",
                          }}
                          spacing={2}
                          alignItems={{
                            xs: "stretch",
                            sm: "center",
                          }}
                        >
                          <Box
                            sx={{
                              flex: 1,
                            }}
                          >
                            <Typography
                              fontWeight={800}
                              color="#062A4B"
                            >
                              Publication Settings
                            </Typography>

                            <Typography
                              variant="body2"
                              color="#6b7280"
                            >
                              Published documents become
                              available to teachers.
                            </Typography>
                          </Box>

                          <Stack
                            direction="row"
                            spacing={1}
                          >
                            <Button
                              variant={
                                form.is_active
                                  ? "contained"
                                  : "outlined"
                              }
                              onClick={() =>
                                setForm(
                                  (previous) => ({
                                    ...previous,
                                    is_active:
                                      !previous.is_active,
                                  })
                                )
                              }
                              sx={{
                                borderRadius: 2,
                                fontWeight: 700,
                                background:
                                  form.is_active
                                    ? "#4FAE22"
                                    : undefined,
                                "&:hover": {
                                  background:
                                    form.is_active
                                      ? "#3f901b"
                                      : undefined,
                                },
                              }}
                            >
                              {form.is_active
                                ? "Active"
                                : "Inactive"}
                            </Button>

                            <Button
                              variant={
                                form.is_published
                                  ? "contained"
                                  : "outlined"
                              }
                              onClick={() =>
                                setForm(
                                  (previous) => ({
                                    ...previous,
                                    is_published:
                                      !previous.is_published,
                                  })
                                )
                              }
                              startIcon={<Publish />}
                              sx={{
                                borderRadius: 2,
                                fontWeight: 700,
                                background:
                                  form.is_published
                                    ? "#C0750D"
                                    : undefined,
                                "&:hover": {
                                  background:
                                    form.is_published
                                      ? "#a4620b"
                                      : undefined,
                                },
                              }}
                            >
                              {form.is_published
                                ? "Published"
                                : "Draft"}
                            </Button>
                          </Stack>
                        </Stack>
                      </Paper>
                    </Grid>

                    <Grid item xs={12}>
                      <Paper
                        variant="outlined"
                        sx={{
                          borderRadius: 3,
                          borderStyle: "dashed",
                          borderWidth: 2,
                          borderColor:
                            fileError
                              ? "#d32f2f"
                              : "#b7c5ce",
                          background:
                            "linear-gradient(135deg,#fbfcfd,#f4f7f8)",
                          p: {
                            xs: 2,
                            md: 4,
                          },
                          textAlign: "center",
                          cursor: "pointer",
                          transition:
                            "220ms ease",
                          "&:hover": {
                            borderColor:
                              "#0F6DAB",
                            background:
                              "#f1f7fb",
                          },
                        }}
                        onClick={() =>
                          fileInputRef.current?.click()
                        }
                      >
                        <input
                          ref={fileInputRef}
                          hidden
                          type="file"
                          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                          onChange={handleFileChange}
                        />

                        <CloudUpload
                          sx={{
                            fontSize: 52,
                            color: "#0F6DAB",
                            mb: 1,
                          }}
                        />

                        <Typography
                          variant="h6"
                          fontWeight={800}
                          color="#062A4B"
                        >
                          {currentFileName
                            ? "Curriculum File Selected"
                            : "Upload Curriculum Document"}
                        </Typography>

                        <Typography
                          variant="body2"
                          color="#6b7280"
                          sx={{
                            mt: 0.7,
                          }}
                        >
                          PDF, Word, Excel or PowerPoint
                          • Maximum 100 MB
                        </Typography>

                        {currentFileName && (
                          <Chip
                            icon={<FilePresent />}
                            label={currentFileName}
                            sx={{
                              mt: 2,
                              background:
                                "#eaf3f8",
                              color: "#062A4B",
                              fontWeight: 700,
                              maxWidth: "100%",
                            }}
                          />
                        )}

                        {file && (
                          <Typography
                            variant="caption"
                            display="block"
                            sx={{
                              mt: 1,
                              color: "#4FAE22",
                              fontWeight: 700,
                            }}
                          >
                            {formatBytes(file.size)}
                          </Typography>
                        )}

                        {fileError && (
                          <FormHelperText
                            error
                            sx={{
                              mt: 1,
                              fontSize: 14,
                            }}
                          >
                            {fileError}
                          </FormHelperText>
                        )}

                        {formErrors.file && (
                          <FormHelperText
                            error
                            sx={{
                              mt: 1,
                              fontSize: 14,
                            }}
                          >
                            {formErrors.file}
                          </FormHelperText>
                        )}
                      </Paper>
                    </Grid>

                    {saving && (
                      <Grid item xs={12}>
                        <Box>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            mb={0.8}
                          >
                            <Typography
                              variant="body2"
                              fontWeight={700}
                              color="#062A4B"
                            >
                              Uploading curriculum...
                            </Typography>

                            <Typography
                              variant="body2"
                              fontWeight={800}
                              color="#0F6DAB"
                            >
                              {uploadProgress}%
                            </Typography>
                          </Stack>

                          <LinearProgress
                            variant="determinate"
                            value={uploadProgress}
                            sx={{
                              height: 8,
                              borderRadius: 10,
                              background:
                                "#e2e8eb",
                              "& .MuiLinearProgress-bar":
                                {
                                  background:
                                    "#4FAE22",
                                },
                            }}
                          />
                        </Box>
                      </Grid>
                    )}

                    <Grid item xs={12}>
                      <Stack
                        direction={{
                          xs: "column",
                          sm: "row",
                        }}
                        spacing={1.5}
                        justifyContent="flex-end"
                      >
                        {onBack && (
                          <Button
                            variant="outlined"
                            onClick={onBack}
                            disabled={saving}
                            sx={{
                              borderRadius: 2,
                              fontWeight: 700,
                              color: "#062A4B",
                              borderColor:
                                "#062A4B",
                            }}
                          >
                            Cancel
                          </Button>
                        )}

                        <Button
                          type="submit"
                          variant="contained"
                          disabled={saving}
                          startIcon={
                            saving ? (
                              <CircularProgress
                                size={18}
                                color="inherit"
                              />
                            ) : (
                              <UploadFile />
                            )
                          }
                          sx={{
                            minWidth: 190,
                            borderRadius: 2,
                            fontWeight: 800,
                            background:
                              "linear-gradient(135deg,#062A4B,#0F6DAB)",
                            boxShadow:
                              "0 8px 20px rgba(6,42,75,.20)",
                            "&:hover": {
                              background:
                                "linear-gradient(135deg,#051f38,#0a5c91)",
                            },
                          }}
                        >
                          {saving
                            ? "Saving..."
                            : isEditing
                              ? "Update Curriculum"
                              : "Post Curriculum"}
                        </Button>
                      </Stack>
                    </Grid>
                  </Grid>
                </Box>
              </CardContent>
            </Card>
          </Grid>


          <Grid
            item
            xs={12}
            lg={4}
          >
            <Stack spacing={2}>
              <Card
                sx={{
                  borderRadius: 3,
                  boxShadow:
                    "0 12px 35px rgba(6,42,75,.10)",
                  border:
                    "1px solid rgba(6,42,75,.08)",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    height: 8,
                    background:
                      "linear-gradient(90deg,#F6C944,#C0750D,#4FAE22)",
                  }}
                />

                <CardContent sx={{ p: 3 }}>
                  <Stack
                    direction="row"
                    spacing={1.2}
                    alignItems="center"
                    mb={2}
                  >
                    <InfoOutlined
                      sx={{
                        color: "#C0750D",
                      }}
                    />

                    <Typography
                      fontWeight={800}
                      color="#062A4B"
                    >
                      Curriculum Guidelines
                    </Typography>
                  </Stack>

                  <Stack spacing={1.8}>
                    <Stack
                      direction="row"
                      spacing={1.2}
                    >
                      <School
                        sx={{
                          color: "#0F6DAB",
                        }}
                      />

                      <Typography
                        variant="body2"
                        color="#53616c"
                      >
                        Assign the curriculum to the
                        correct academic year and grade/class.
                      </Typography>
                    </Stack>

                    <Stack
                      direction="row"
                      spacing={1.2}
                    >
                      <SubjectIcon
                        sx={{
                          color: "#4FAE22",
                        }}
                      />

                      <Typography
                        variant="body2"
                        color="#53616c"
                      >
                        A subject may be selected for a
                        subject-specific curriculum.
                      </Typography>
                    </Stack>

                    <Stack
                      direction="row"
                      spacing={1.2}
                    >
                      <CheckCircle
                        sx={{
                          color: "#C0750D",
                        }}
                      />

                      <Typography
                        variant="body2"
                        color="#53616c"
                      >
                        Published documents are available
                        to authorized teachers.
                      </Typography>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>


              <Card
                sx={{
                  borderRadius: 3,
                  background:
                    "linear-gradient(145deg,#062A4B,#0A0F12)",
                  color: "#fff",
                  overflow: "hidden",
                  position: "relative",
                  minHeight: 250,
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    width: 180,
                    height: 180,
                    borderRadius: "50%",
                    background:
                      "rgba(246,201,68,.08)",
                    right: -60,
                    top: -60,
                  }}
                />

                <Box
                  sx={{
                    position: "absolute",
                    width: 120,
                    height: 120,
                    borderRadius: "50%",
                    background:
                      "rgba(79,174,34,.08)",
                    left: -40,
                    bottom: -50,
                  }}
                />

                <CardContent
                  sx={{
                    position: "relative",
                    p: 3,
                  }}
                >
                  <LibraryBooks
                    sx={{
                      fontSize: 52,
                      color: "#F6C944",
                      mb: 1,
                    }}
                  />

                  <Typography
                    variant="h5"
                    fontWeight={800}
                    sx={{
                      color: "#fff",
                    }}
                  >
                   ZPA Curriculum Library
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      mt: 1,
                      color:
                        "rgba(255,255,255,.72)",
                      lineHeight: 1.8,
                    }}
                  >
                    Build an organized digital library
                    where teachers can quickly discover,
                    review and download official curriculum
                    resources.
                  </Typography>
                </CardContent>
              </Card>
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};


export default CurriculumPost;